import { ActionCreator, ReducerTypes, on } from '@ngrx/store';

import { DoodCartModel, DoodReconstructedCartItem } from '@core/models/cart.model';
import { createActiveStorePiece } from '@store/_abstract/active';
import { createHydratedStorePiece } from '@store/_abstract/hydrated';
import { PRODUCT_STORE_COMPONENT_ACTIONS } from '@store/product/product.action';
import { SETTINGS_STORE_LOCAL_ACTIONS } from '@store/settings/settings.action';
import { CART_ACTIONS } from './cart.action';

import {
  CartState,
  CART_STORE_INITIAL_STATE,
  CART_STORE_KEY,
  CART_STORE_ACTIVE_ADAPTER,
} from './cart.state';
import {
  CART_STORE_ACTIVE_ACTIONS,
  CART_STORE_COMPONENT_ACTIONS,
  CART_STORE_HYDRATION_ACTIONS,
  CART_STORE_LOCAL_ACTIONS,
} from './cart.action';

export const CART_STORE_API_PIECE: ReducerTypes<CartState, readonly ActionCreator[]>[] = [];

export const CART_STORE_LOCAL_PIECE: ReducerTypes<CartState, readonly ActionCreator[]>[] = [
  // Comment
  on(CART_STORE_LOCAL_ACTIONS.setComment, (state, { comment }): CartState => {
    return { ...state, comment };
  }),
  on(CART_STORE_LOCAL_ACTIONS.updateComment, (state, { comment }): CartState => {
    return {
      ...state,
      comment: { ...(state.comment ?? { type: '' }), ...comment },
    };
  }),
  on(CART_STORE_LOCAL_ACTIONS.resetComment, (state): CartState => {
    return { ...state, comment: null };
  }),

  // Edit Item
  on(CART_STORE_LOCAL_ACTIONS.setEditItem, (state, { item }): CartState => {
    return { ...state, edit_item: item };
  }),
  on(CART_STORE_LOCAL_ACTIONS.updateEditItem, (state, { item }): CartState => {
    return {
      ...state,
      edit_item: { ...state.edit_item, ...(item as DoodReconstructedCartItem) },
    };
  }),
  on(CART_STORE_LOCAL_ACTIONS.resetComment, (state): CartState => {
    return { ...state, edit_item: null };
  }),

  // Last Update
  on(CART_STORE_LOCAL_ACTIONS.updateLastUpdate, (state, { last_update }): CartState => {
    return { ...state, last_update };
  }),
  on(CART_STORE_LOCAL_ACTIONS.resetComment, (state): CartState => {
    return { ...state, comment: null };
  }),

  // Misc
  on(CART_STORE_LOCAL_ACTIONS.reset, (): CartState => {
    return CART_STORE_INITIAL_STATE;
  }),
];

export const CART_STORE_COMPONENT_PIECE: ReducerTypes<CartState, readonly ActionCreator[]>[] = [
  on(CART_STORE_COMPONENT_ACTIONS.updateState, (state, { changes }): CartState => {
    return {
      ...state,
      ...changes,
    };
  }),
  on(PRODUCT_STORE_COMPONENT_ACTIONS.selectProduct, (state, { productOrigin }): CartState => {
    return {
      ...state,
      last2SelectedProductOrigin: [productOrigin, state.last2SelectedProductOrigin[0]],
    };
  }),
  on(
    CART_ACTIONS.updateProductInCart,
    (state, { cartItem, isNewItem, cartId, marketplaceId, lastEditedCartItem }): CartState => {
      const currentCart = state.entities[cartId];
      if (!currentCart) {
        return state;
      }

      const updatedProducts = isNewItem
        ? [...currentCart.products, cartItem]
        : currentCart.products.map(item => (item.id === cartItem.id ? cartItem : item));

      const nextState = CART_STORE_ACTIVE_ADAPTER.upsertOne(
        {
          ...currentCart,
          marketplace: marketplaceId,
          products: updatedProducts,
        },
        state,
      );
      return { ...nextState, lastEditedCartItem };
    },
  ),
  // Cross-store sync: mirror Settings.parameters.wanted_at onto the active cart
  // so order validation always sends the latest slot selected by the user,
  // even when the slot was selected outside the cart flow (e.g. shop search).
  on(SETTINGS_STORE_LOCAL_ACTIONS.updateParameters, (state, { parameters }): CartState => {
    if (!parameters || !('wanted_at' in parameters)) {
      return state;
    }
    const activeId = state.active;
    if (activeId === null) {
      return state;
    }
    const currentCart = state.entities[activeId];
    if (!currentCart) {
      return state;
    }
    const nextWantedAt = parameters.wanted_at ?? null;
    if (currentCart.wanted_at === nextWantedAt) {
      return state;
    }
    return CART_STORE_ACTIVE_ADAPTER.upsertOne({ ...currentCart, wanted_at: nextWantedAt }, state);
  }),
];

export const CART_STORE_ACTIVE_PIECE = createActiveStorePiece<CartState, DoodCartModel>({
  adapter: CART_STORE_ACTIVE_ADAPTER,
  actions: CART_STORE_ACTIVE_ACTIONS,
});

export const CART_STORE_HYDRATION_PIECE = createHydratedStorePiece<CartState>(
  CART_STORE_KEY,
  CART_STORE_HYDRATION_ACTIONS,
);
